// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
import MathUtils from '../../math/MathUtils';
import AnimationClipInstance from '../../animationpack/clip/AnimationClipInstance';
import Source from '../../animationpack/blendtree/Source';

/**
 * A blend tree leaf node that samples and returns values from the channels of an AnimationClip.
 */
class ClipSource extends Source {
  /**
   * @param {AnimationClip} clip the clip to use.
   * @param {string} [filter] 'Exclude' or 'Include'
   * @param {Array<string>} [channelNames]
   * @param {World} [world] - the sumerian world this clip belongs to
   */
  constructor(clip, filter, channelNames, world) {
    super();
    this._clip = clip;

    if (world === undefined) {
      console.warn('[Sumerian] ClipSource object constructed without a world object. Playback may be erratic.');
    }
    this._clipInstance = new AnimationClipInstance(world);

    this._filterChannels = {};
    this._filter = null;
    this.setFilter(filter, channelNames);

    this._startTime = -Infinity;
    this._endTime = Infinity;

    this.currentLoop = 0;
  }

  /**
   * Sets the filter on the joints which the clipsource will affect
   * @param {string} [filter] 'Exclude' or 'Include'
   * @param {Array<string>} [channelNames]
   */
  setFilter(filter, channelNames) {
    if (filter && channelNames) {
      this._filter = (['Exclude', 'Include'].indexOf(filter) > -1) ? filter : null;
      for (let i = 0; i < channelNames.length; i++) {
        this._filterChannels[channelNames[i]] = true;
      }
    } else {
      this._filter = null;
    }
  }

  /**
   * Set the clip start time
   * @param {number} newStartTime
   */
  setStartTime(newStartTime) {
    this._startTime = newStartTime;
  }

  /**
   * Sets the current time and moves the {@link AnimationClipInstance} forward
   * @param {number} globalTime
   * @private
   */
  setTime(globalTime) {
    const instance = this._clipInstance;
    if (typeof instance._startTime !== 'number') {
      instance._startTime = globalTime;
    }
    let clockTime;
    let duration;
    if (instance._active) {
      if (instance._timeScale !== 0.0) {
        instance._prevUnscaledClockTime = globalTime - instance._startTime;
        clockTime = instance._timeScale * instance._prevUnscaledClockTime;
        instance._prevClockTime = clockTime;
      } else {
        clockTime = instance._prevClockTime;
      }

      const maxTime = Math.min(this._clip._maxTime, this._endTime);
      const minTime = this._startTime;
      duration = maxTime - minTime;
      if (maxTime === -1) {
        return false;
      }

      // Check for looping
      if (duration !== 0) {
        this.currentLoop = Math.floor(clockTime / duration);
        if (instance._loopCount === -1 || (instance._loopCount > 0 && duration * instance._loopCount > Math.abs(clockTime))) {
          if (clockTime < 0) {
            clockTime *= -1;
            clockTime %= duration;
            clockTime = duration - clockTime;
          } else {
            clockTime %= duration;
          }
        }
        clockTime += minTime;

        if (clockTime > maxTime || clockTime < minTime) {
          clockTime = MathUtils.clamp(clockTime, minTime, maxTime);
          // deactivate this instance of the clip
          instance._active = false;
        }
      }

      // update the clip with the correct clip local time.
      this._clip.update(clockTime, instance);
    }
    return instance._active;
  }

  /**
   * Sets start time of clipinstance. If set to current time, clip is reset
   * @param {number} globalTime
   * @private
   */
  resetClips(globalTime) {
    this._clipInstance._startTime = typeof globalTime !== 'undefined' ? globalTime : 0;
    this._clipInstance._active = true;
  }

  /**
   * @private
   */
  shiftClipTime(shiftTime) {
    this._clipInstance._startTime += shiftTime;
    this._clipInstance._active = true;  // ?
  }

  /**
   * @private
   */
  setTimeScale(timeScale) {
    this._clipInstance.setTimeScale(timeScale);
  }

  /**
   * @returns {boolean} if clipsource is active
   * @private
   */
  isActive() {
    return this._clipInstance._active && (this._clip._maxTime !== -1);
  }

  /**
   * @returns a source data mapping for the channels in this clip source
   * @private
   */
  getSourceData() {
    if (!this._filter || !this._filterChannels) {
      return this._clipInstance._clipStateObjects;
    }
    const cso = this._clipInstance._clipStateObjects;
    const rVal = {};

    const filter = (this._filter === 'Include');

    for (const key in cso) {
      if ((this._filterChannels[key] !== undefined) === filter) {
        rVal[key] = cso[key];
      }
    }
    return rVal;
  }

  /**
   * @returns {ClipSource}
   */
  clone() {
    const cloned = new ClipSource(this._clip);

    cloned._clipInstance = this._clipInstance.clone();

    cloned._filter = this._filter;

    for (const key in this._filterChannels) {
      cloned._filterChannels[key] = this._filterChannels[key];
    }

    cloned.setStartTime(this._startTime);
    cloned._endTime = this._endTime;

    return cloned;
  }
}

export default ClipSource;
